import { sendMedia } from "../../api/whatsapp.js";

type RecorderState = {
  mediaRecorder: MediaRecorder;
  stream: MediaStream;
  chunks: BlobPart[];
  mime: string;
  cancelled: boolean;
  startedAt: number;
  stopping: boolean;
};

type StartRecordingOptions = {
  chatJid: string;
  onRecordingChange: (recording: boolean) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  onSent: () => void;
};

let recorderState: RecorderState | null = null;
const MIN_RECORDING_MS = 700;
const MIN_RECORDING_BYTES = 900;
const RECORDER_MIME_TYPES = [
  "audio/ogg;codecs=opus",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "",
];

export async function startRecording({
  chatJid,
  onRecordingChange,
  onError,
  onSuccess,
  onSent,
}: StartRecordingOptions) {
  if (recorderState) return;
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    onError("Microfone nao suportado neste navegador");
    return;
  }
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (_) {
    onError("Permissao de microfone negada");
    return;
  }

  const mime = pickRecorderMime();
  const chunks: BlobPart[] = [];
  let mediaRecorder: MediaRecorder;
  try {
    mediaRecorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  } catch (_) {
    stream.getTracks().forEach((track) => track.stop());
    onError("Nao foi possivel iniciar a gravacao neste navegador");
    return;
  }

  const current: RecorderState = {
    mediaRecorder,
    stream,
    chunks,
    mime: mediaRecorder.mimeType || mime || "audio/webm",
    cancelled: false,
    startedAt: Date.now(),
    stopping: false,
  };
  recorderState = current;

  mediaRecorder.addEventListener("dataavailable", (event) => {
    if (event.data?.size) chunks.push(event.data);
  });
  mediaRecorder.addEventListener("error", (event) => {
    current.cancelled = true;
    stream.getTracks().forEach((track) => track.stop());
    if (recorderState === current) recorderState = null;
    onRecordingChange(false);
    const detail = (event as Event & { error?: DOMException }).error?.message;
    onError(detail || "Falha ao gravar audio");
  });
  mediaRecorder.addEventListener("stop", () => {
    stream.getTracks().forEach((track) => track.stop());
    if (recorderState === current) recorderState = null;
    onRecordingChange(false);
    if (current.cancelled) return;
    const mimeType = mediaRecorder.mimeType || current.mime || "audio/webm";
    const blob = new Blob(chunks, { type: mimeType });
    if (Date.now() - current.startedAt < MIN_RECORDING_MS || blob.size < MIN_RECORDING_BYTES) {
      onError("Audio muito curto. Grave por pelo menos 1 segundo.");
      return;
    }
    const ext = audioExtensionFor(mimeType);
    const file = new File([blob], `voz.${ext}`, { type: blob.type });
    sendMedia({ chatJid, kind: "audio", file, ptt: isVoiceCompatibleMime(mimeType) }).then(() => {
      onSent();
      onSuccess("Audio enviado");
    }).catch((err: unknown) => {
      onError(err instanceof Error ? err.message : "Falha ao enviar audio");
    });
  });

  try {
    mediaRecorder.start(500);
  } catch (_) {
    stream.getTracks().forEach((track) => track.stop());
    if (recorderState === current) recorderState = null;
    onError("Nao foi possivel iniciar a gravacao neste navegador");
    return;
  }
  onRecordingChange(true);
}

export function cancelRecording() {
  stopActiveRecording(true);
}

export function finishRecording() {
  stopActiveRecording(false);
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, "0")}`;
}

function stopActiveRecording(cancelled: boolean) {
  const current = recorderState;
  if (!current || current.stopping) return;
  current.cancelled = cancelled;
  current.stopping = true;
  try {
    if (current.mediaRecorder.state === "recording") current.mediaRecorder.requestData();
  } catch (_) {
    /* noop */
  }
  window.setTimeout(() => {
    try {
      if (current.mediaRecorder.state !== "inactive") {
        current.mediaRecorder.stop();
        return;
      }
    } catch (_) {
      /* fall through */
    }
    current.stream.getTracks().forEach((track) => track.stop());
    if (recorderState === current) recorderState = null;
  }, 80);
}

function pickRecorderMime(): string {
  return RECORDER_MIME_TYPES.find((item) => !item || MediaRecorder.isTypeSupported(item)) || "";
}

function audioExtensionFor(mime: string): string {
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("mp4") || mime.includes("mpeg")) return "m4a";
  if (mime.includes("webm")) return "webm";
  return "webm";
}

function isVoiceCompatibleMime(mime: string): boolean {
  const normalized = mime.toLowerCase();
  return normalized.includes("audio/ogg") && normalized.includes("opus");
}
